import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import ConfirmDeleteModal from '@/components/ConfirmDeleteModal';
import { AdminPageSections, CollapsiblePanel, StickyFormBar } from '@/components/common';
import DataModelHelp from '@/components/DataModelHelp';
import EditSubscriptionModal from '@/components/EditSubscriptionModal';
import JobProgressCard from '@/components/jobs/JobProgressCard';
import RecentJobsPanel from '@/components/jobs/RecentJobsPanel';
import PageHeader from '@/components/PageHeader';
import {
  DataTable,
  TruncatedCell,
  TableActionBar,
  TABLE_ACTION_MIN_WIDTH,
  tableActionsColumnClass,
  type DataTableColumn,
} from '@/components/table';
import { exportTableCsv } from '@/utils/exportTableCsv';
import {
  EmptyState,
  ErrorBanner,
  RecipientCell,
  SourceChips,
  StatusBadge,
} from '@/components/ui';
import { useToast } from '@/components/Toast';
import { useMe } from '@/context/MeContext';
import { useWorkspaceScopedEffect } from '@/hooks/useWorkspaceScopedEffect';
import { planWarningMessage } from '@/utils/planWarning';
import {
  duplicateSubscriptionMessage,
  formatApiError,
  isDuplicateSubscriptionError,
} from '@/utils/formatApiError';
import { markDiagnosticsStale } from '@/utils/diagnosticsRefresh';
import {
  canCreateSubscription as userCanCreateSubscription,
  canDeleteSubscription as userCanDeleteSubscription,
  canEditSubscription as userCanEditSubscription,
  canViewSubscriptions,
} from '@/utils/navCapabilities';
import { subscriptionRecipientInput } from '@/utils/recipientDisplay';
import { visibilityBadgeLabel } from '@/utils/recipientVisibility';
import { formatFiltersSummary, isBroadSearch } from '@/utils/searchFilters';
import type { Delivery, Recipient, Search, Subscription } from '@/types';

type SubDeliveryStats = {
  lastDelivery: string | null;
  failureCount: number;
};

const PANEL_SCOPE = 'subscriptions-page';

export default function SubscriptionsPage() {
  const { toast } = useToast();
  const { me } = useMe();
  const readOnly = me?.permissions.readOnly ?? false;
  const canView = canViewSubscriptions(me);
  const canCreate = userCanCreateSubscription(me);
  const canEdit = userCanEditSubscription(me);
  const canDelete = userCanDeleteSubscription(me);
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [searches, setSearches] = useState<Search[]>([]);
  const [deliveryStats, setDeliveryStats] = useState<Map<string, SubDeliveryStats>>(new Map());
  const [userId, setUserId] = useState('');
  const [searchId, setSearchId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editSub, setEditSub] = useState<Subscription | null>(null);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [activeSubscriptionId, setActiveSubscriptionId] = useState<string | null>(null);

  const searchById = useMemo(() => new Map(searches.map((s) => [s.id, s])), [searches]);

  const buildDeliveryStats = (deliveries: Delivery[]): Map<string, SubDeliveryStats> => {
    const map = new Map<string, SubDeliveryStats>();
    for (const d of deliveries) {
      if (!d.subscription_id) continue;
      const cur = map.get(d.subscription_id) ?? { lastDelivery: null, failureCount: 0 };
      if (!cur.lastDelivery || d.sent_at > cur.lastDelivery) {
        cur.lastDelivery = d.sent_at;
      }
      if (d.status === 'FAILED') cur.failureCount += 1;
      map.set(d.subscription_id, cur);
    }
    return map;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u, sr, deliveries] = await Promise.all([
        api.subscriptions.list(),
        api.recipients.list(),
        api.searches.list(),
        api.deliveriesPaginated(1, 200).catch(() => ({ items: [] as Delivery[] })),
      ]);
      setSubs(s);
      setRecipients(u);
      setSearches(sr);
      setDeliveryStats(buildDeliveryStats(deliveries.items));
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, []);

  useWorkspaceScopedEffect(() => {
    setSubs([]);
    setRecipients([]);
    setSearches([]);
    setDeliveryStats(new Map());
    setLoading(true);
    void load();
  }, [load]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    try {
      const { planWarning } = await api.subscriptions.create({
        recipient_id: userId,
        search_id: searchId,
      });
      setUserId('');
      setSearchId('');
      toast('Subscription created — alerts are active', 'success');
      const warn = planWarningMessage(planWarning);
      if (warn) toast(warn, 'error');
      await load();
    } catch (err) {
      if (isDuplicateSubscriptionError(err)) {
        toast(duplicateSubscriptionMessage(err), 'error');
        setError('');
        return;
      }
      setError(formatApiError(err, 'Create failed'));
    }
  }

  const stopSubscription = useCallback(
    async (s: Subscription) => {
      if (!s.is_enabled) return;
      await api.subscriptions.update(s.id, { is_enabled: false });
      markDiagnosticsStale();
      toast('Subscription stopped — history preserved', 'info');
      await load();
    },
    [toast, load]
  );

  const runScrape = useCallback(
    async (s: Subscription) => {
      setRunningId(s.id);
      try {
        const result = await api.scrape.runSubscription(s.id);
        if (result.job_id) {
          setActiveJobId(result.job_id);
          setActiveSubscriptionId(s.id);
        }
        toast(
          result.jobs_queued
            ? result.job_id
              ? `Scrape job queued (${result.total_items ?? result.jobs_queued} items)`
              : `Queued ${result.jobs_queued} scrape job(s)`
            : 'No jobs queued — check search text and sources',
          result.jobs_queued ? 'success' : 'error'
        );
        result.warnings.forEach((w) => toast(w, 'error'));
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Scrape failed', 'error');
      } finally {
        setRunningId(null);
      }
    },
    [toast]
  );

  const enableSubscription = useCallback(
    async (s: Subscription) => {
      if (s.is_enabled) return;
      await api.subscriptions.update(s.id, { is_enabled: true });
      markDiagnosticsStale();
      toast('Subscription enabled — alerts are active', 'success');
      await load();
    },
    [toast, load]
  );

  const filtersForSub = useCallback(
    (sub: Subscription): string => {
      const search = searchById.get(sub.search_id);
      return formatFiltersSummary(search?.filters_json, sub.search_name ?? search?.name);
    },
    [searchById]
  );

  const isBroadSub = useCallback(
    (sub: Subscription): boolean => {
      const search = searchById.get(sub.search_id);
      return isBroadSearch(search?.filters_json, sub.search_name ?? search?.name);
    },
    [searchById]
  );

  const broadEnabledSubs = useMemo(
    () => subs.filter((s) => s.is_enabled && isBroadSub(s)),
    [subs, isBroadSub]
  );

  const defaultMeSub = useMemo(
    () =>
      subs.find(
        (s) =>
          s.is_enabled &&
          (s.search_name?.toLowerCase() === 'default' ||
            searchById.get(s.search_id)?.name?.toLowerCase() === 'default') &&
          (s.recipient_chat_id === 'me' || s.user_telegram_chat_id === 'me'),
      ),
    [subs, searchById],
  );

  const sourcesListForSub = useCallback(
    (sub: Subscription): string[] => {
      if (sub.search_source_names?.length) {
        return sub.search_source_names;
      }
      const search = searchById.get(sub.search_id);
      return search?.source_names ?? [];
    },
    [searchById]
  );

  const sourceCountForSub = useCallback(
    (sub: Subscription): number => {
      if (sub.search_source_names?.length) return sub.search_source_names.length;
      return searchById.get(sub.search_id)?.source_names?.length ?? 0;
    },
    [searchById]
  );

  const subscriptionColumns = useMemo<DataTableColumn<Subscription>[]>(
    () => [
      {
        id: 'recipient',
        header: 'Recipient',
        width: 160,
        minWidth: 120,
        maxWidth: 260,
        render: (s) => (
          <RecipientCell recipient={subscriptionRecipientInput(s)} linkTo="/recipients" />
        ),
      },
      {
        id: 'search',
        header: 'Search',
        width: 180,
        minWidth: 140,
        maxWidth: 320,
        render: (s) => {
          const broad = isBroadSub(s);
          const sourceCount = sourceCountForSub(s);
          return (
            <div className="recipient-cell">
              <Link to={`/searches/${s.search_id}/links`} className="table-link recipient-cell__primary">
                <TruncatedCell value={s.search_name} />
              </Link>
              {broad && s.is_enabled && (
                <StatusBadge variant="warning" title="No filters — matches broad listings">
                  Broad
                </StatusBadge>
              )}
              {sourceCount > 0 && (
                <span className="search-cell__secondary">
                  {sourceCount} source{sourceCount === 1 ? '' : 's'}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'createdBy',
        header: 'Created by',
        width: 130,
        minWidth: 100,
        maxWidth: 180,
        sortValue: (s) => s.owner_name || s.owner_user_id || '',
        render: (s) => <TruncatedCell value={s.owner_name || s.owner_user_id || '—'} />,
      },
      {
        id: 'visibility',
        header: 'Visibility',
        width: 110,
        minWidth: 90,
        maxWidth: 140,
        sortValue: (s) => s.visibility || 'private',
        render: (s) => (
          <StatusBadge variant={s.visibility === 'workspace' || s.visibility === 'organization' ? 'info' : 'neutral'}>
            {visibilityBadgeLabel(s.visibility)}
          </StatusBadge>
        ),
      },
      {
        id: 'filters',
        header: 'Filters',
        width: 200,
        minWidth: 140,
        maxWidth: 360,
        render: (s) => {
          const broad = isBroadSub(s);
          const filterSummary = filtersForSub(s);
          return (
            <TruncatedCell
              value={filterSummary}
              className={broad ? 'filters-cell--broad' : undefined}
            />
          );
        },
      },
      {
        id: 'sources',
        header: 'Sources',
        width: 140,
        minWidth: 100,
        maxWidth: 220,
        render: (s) => <SourceChips sources={sourcesListForSub(s)} variant="compact" />,
      },
      {
        id: 'status',
        header: 'Status',
        width: 90,
        minWidth: 72,
        maxWidth: 100,
        resizable: false,
        render: (s) => (
          <StatusBadge variant={s.is_enabled ? 'success' : 'neutral'}>
            {s.is_enabled ? 'Active' : 'Stopped'}
          </StatusBadge>
        ),
      },
      {
        id: 'lastDelivery',
        header: 'Last delivery',
        width: 140,
        minWidth: 110,
        maxWidth: 180,
        render: (s) => {
          const stats = deliveryStats.get(s.id);
          return (
            <TruncatedCell
              value={
                stats?.lastDelivery ? new Date(stats.lastDelivery).toLocaleString() : '—'
              }
              title={stats?.lastDelivery || undefined}
            />
          );
        },
      },
      {
        id: 'failures',
        header: 'Failures',
        width: 80,
        minWidth: 64,
        maxWidth: 100,
        resizable: false,
        render: (s) => {
          const failures = deliveryStats.get(s.id)?.failureCount ?? 0;
          return failures > 0 ? (
            <StatusBadge variant="danger">{failures}</StatusBadge>
          ) : (
            <span className="muted">0</span>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        width: TABLE_ACTION_MIN_WIDTH.subscriptions,
        minWidth: TABLE_ACTION_MIN_WIDTH.subscriptions,
        resizable: false,
        actions: true,
        className: tableActionsColumnClass('subscriptions'),
        render: (s) =>
          canEdit || canDelete ? (
            <TableActionBar
              danger={
                canDelete ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => setDeleteId(s.id)}
                    aria-label={`Remove subscription for ${s.search_name}`}
                  >
                    Remove
                  </button>
                ) : undefined
              }
            >
              <Link to={`/searches/${s.search_id}/links`} className="btn btn-sm btn-ghost">
                View results
              </Link>
              {s.is_enabled && (
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  disabled={runningId === s.id}
                  onClick={() => runScrape(s)}
                >
                  {runningId === s.id ? 'Running…' : 'Run scrape'}
                </button>
              )}
              {canEdit ? (
                <>
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditSub(s)}>
                    Edit
                  </button>
                  {s.is_enabled ? (
                    <button type="button" className="btn btn-sm btn-primary" onClick={() => stopSubscription(s)}>
                      Stop
                    </button>
                  ) : (
                    <button type="button" className="btn btn-sm" onClick={() => enableSubscription(s)}>
                      Enable
                    </button>
                  )}
                </>
              ) : null}
            </TableActionBar>
          ) : (
            <TableActionBar>
              <Link to={`/searches/${s.search_id}/links`} className="btn btn-sm btn-ghost">
                View results
              </Link>
            </TableActionBar>
          ),
      },
    ],
    [
      canEdit,
      canDelete,
      deliveryStats,
      runningId,
      isBroadSub,
      filtersForSub,
      sourcesListForSub,
      sourceCountForSub,
      stopSubscription,
      enableSubscription,
      runScrape,
    ]
  );

  if (!canView) {
    return (
      <div className="admin-page admin-page--subscriptions">
        <PageHeader
          title="Subscriptions"
          subtitle="Recipient → search links — who gets notified about which listings"
        />
        <p className="muted">You do not have permission to view subscriptions for this workspace.</p>
      </div>
    );
  }

  return (
    <div className="admin-page admin-page--subscriptions">
      <PageHeader
        title="Subscriptions"
        subtitle="Recipient → search links — who gets notified about which listings"
      />

      <AdminPageSections>
        <CollapsiblePanel
          id="help"
          storageScope={PANEL_SCOPE}
          title="Data model"
          subtitle="How searches, subscriptions, and deliveries relate"
          defaultExpanded={false}
        >
          <DataModelHelp />
        </CollapsiblePanel>

        {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

        {(defaultMeSub || broadEnabledSubs.length > 0) && (
          <CollapsiblePanel
            id="alerts"
            storageScope={PANEL_SCOPE}
            title="Alerts"
            defaultExpanded
          >
            {defaultMeSub && (
              <div className="broad-search-banner" role="status">
                <strong>Broad search active:</strong> <code>me → default</code> has no filters and can
                send <em>all</em> vehicle listings from its sources (including Honda Civic Type R).
                Pave&apos;s Honda alerts use a separate subscription.
                {!readOnly && (
                  <div className="broad-search-banner__actions">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => stopSubscription(defaultMeSub)}
                    >
                      Stop default subscription
                    </button>
                  </div>
                )}
              </div>
            )}
            {!defaultMeSub && broadEnabledSubs.length > 0 && (
              <div className="broad-search-banner broad-search-banner--muted" role="status">
                <strong>{broadEnabledSubs.length} broad search subscription(s)</strong> — no text filters;
                may match many listings. Review filters in the table below.
              </div>
            )}
          </CollapsiblePanel>
        )}

        {!readOnly && canCreate && (
          <CollapsiblePanel
            id="create-subscription"
            storageScope={PANEL_SCOPE}
            title="Create subscription"
            subtitle="Link a recipient to a search"
            defaultExpanded
          >
            <form className="subscription-create-form" onSubmit={create}>
              <div className="admin-form-grid">
                <label>
                  Recipient
                  <select
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    required
                    aria-label="Recipient"
                  >
                    <option value="">Select recipient…</option>
                    {recipients.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.display_name || (r.telegram_username ? `@${r.telegram_username}` : r.chat_id)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Search
                  <select
                    value={searchId}
                    onChange={(e) => setSearchId(e.target.value)}
                    required
                    aria-label="Search"
                  >
                    <option value="">Select search…</option>
                    {searches.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <StickyFormBar>
                <button type="submit" className="btn btn-primary">
                  Subscribe
                </button>
              </StickyFormBar>
            </form>
          </CollapsiblePanel>
        )}

        <CollapsiblePanel
          id="subscription-list"
          storageScope={PANEL_SCOPE}
          title="Subscriptions"
          badge={loading ? '…' : subs.length}
          defaultExpanded
          loading={loading}
          unmountWhenCollapsed
        >
          {subs.length === 0 ? (
            <EmptyState
              title="No subscriptions configured yet"
              description="Connect a recipient to a search to start sending listing alerts."
              action={
                !readOnly && canCreate ? (
                  <span className="muted">Expand Create subscription above to link a recipient.</span>
                ) : (
                  <Link to="/recipients" className="btn btn-sm">
                    View recipients
                  </Link>
                )
              }
            />
          ) : (
            <DataTable
              columns={subscriptionColumns}
              rows={subs}
              getRowKey={(s) => s.id}
              getRowClassName={(s) => (!s.is_enabled ? 'row-muted' : undefined)}
              columnStorageKey="subscriptions"
              toolbar={{
                searchPlaceholder: 'Filter subscriptions…',
                onRefresh: () => load(),
                onExportCsv: () => exportTableCsv(subscriptionColumns, subs, 'subscriptions.csv'),
              }}
            />
          )}
        </CollapsiblePanel>

        {activeJobId && (
          <CollapsiblePanel
            id="active-job"
            storageScope={PANEL_SCOPE}
            title="Active scrape job"
            defaultExpanded
          >
            <JobProgressCard
              jobId={activeJobId}
              title="Subscription scrape job"
              onDismiss={() => setActiveJobId(null)}
            />
          </CollapsiblePanel>
        )}

        {activeSubscriptionId && (
          <CollapsiblePanel
            id="recent-jobs"
            storageScope={PANEL_SCOPE}
            title="Recent jobs"
            defaultExpanded
          >
            <RecentJobsPanel
              subscriptionId={activeSubscriptionId}
              onSelectJob={(id) => setActiveJobId(id)}
            />
          </CollapsiblePanel>
        )}
      </AdminPageSections>

      {editSub && (
        <EditSubscriptionModal
          subscription={editSub}
          recipients={recipients}
          searches={searches}
          onClose={() => setEditSub(null)}
          onSaved={(msg) => {
            toast(msg, 'success');
            load();
          }}
        />
      )}

      {deleteId && (
        <ConfirmDeleteModal
          entityType="subscription"
          entityId={deleteId}
          onClose={() => setDeleteId(null)}
          onDone={(msg) => {
            toast(msg, 'success');
            load();
          }}
        />
      )}
    </div>
  );
}
